import React, { useEffect, useMemo } from 'react';
import Icon from '../../components/Icon';

function getNotificationTone(notification = {}) {
  const type = String(notification.type || '').toLowerCase();
  if (type.includes('leeg')) return 'error';
  if (type.includes('laag') || type.includes('expiring') || type.includes('verloopt')) return 'warning';
  return 'info';
}

function getNotificationIcon(notification = {}) {
  const type = String(notification.type || '').toLowerCase();
  if (type.includes('onderdeel')) return 'tools';
  if (type.includes('garantie')) return 'shield';
  if (type.includes('bijzonderheid')) return 'clipboard';
  return 'bell';
}

export default function NotificationPopup({
  open,
  notifications = [],
  gelezenNotificaties = {},
  setGelezenNotificaties,
  onNavigate,
  onClose,
  onMarkAllRead,
}) {
  const unreadCount = useMemo(
    () => notifications.filter((notification) => !gelezenNotificaties?.[notification.id]).length,
    [gelezenNotificaties, notifications]
  );

  useEffect(() => {
    if (!open) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        onClose?.();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose, open]);

  if (!open) return null;

  const markNotificationRead = (notification) => {
    const newGelezen = { ...gelezenNotificaties, [notification.id]: true };
    setGelezenNotificaties?.(newGelezen);
    localStorage.setItem('gelezenNotificaties', JSON.stringify(newGelezen));
  };

  const handleNotificationClick = (notification) => {
    markNotificationRead(notification);
    if (notification.navigateTo) {
      onNavigate?.(notification.navigateTo);
    }
    onClose?.();
  };

  const handleMarkAllRead = () => {
    onMarkAllRead?.();
    onClose?.();
  };

  return (
    <>
      <div className="notification-popup-overlay" onClick={onClose} aria-hidden="true" />
      <section
        className="notification-center"
        role="dialog"
        aria-modal="true"
        aria-labelledby="notification-center-title"
      >
        <header className="notification-center__header">
          <div>
            <h2 id="notification-center-title" className="notification-center__title">
              <span className="notification-center__title-icon">
                <Icon name="bell" size={20} />
              </span>
              Notificaties
            </h2>
            <p className="notification-center__subtitle">
              {unreadCount > 0
                ? `${unreadCount} ongelezen melding${unreadCount === 1 ? '' : 'en'}`
                : 'Je bent helemaal bij.'}
            </p>
          </div>
          <button
            type="button"
            className="notification-center__close"
            onClick={onClose}
            aria-label="Notificaties sluiten"
          >
            <Icon name="close" size={18} />
          </button>
        </header>

        <div className="notification-center__body">
          {notifications.length === 0 ? (
            <div className="notification-empty">
              <span className="notification-empty__icon">
                <Icon name="check" size={28} />
              </span>
              <h3>Geen notificaties</h3>
              <p>Je bent helemaal bij.</p>
            </div>
          ) : (
            notifications.map((notification) => {
              const isUnread = !gelezenNotificaties?.[notification.id];
              const tone = getNotificationTone(notification);
              return (
                <article
                  key={notification.id}
                  className={`notification-item notification-item--${tone} ${isUnread ? 'notification-item--unread' : 'notification-item--read'}`}
                  onClick={() => handleNotificationClick(notification)}
                  tabIndex={0}
                  role="button"
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      handleNotificationClick(notification);
                    }
                  }}
                >
                  <span className="notification-item__icon">
                    <Icon name={getNotificationIcon(notification)} size={18} />
                  </span>
                  <div className="notification-item__content">
                    <h3 className="notification-item__title">{notification.title}</h3>
                    <p className="notification-item__description">{notification.message}</p>
                    <p className="notification-item__meta">Operationele melding</p>
                  </div>
                  {isUnread ? <span className="notification-item__unread-dot" aria-label="Ongelezen" /> : null}
                </article>
              );
            })
          )}
        </div>

        <footer className="notification-center__footer">
          <button
            type="button"
            className="notification-center__mark-read"
            onClick={handleMarkAllRead}
            disabled={unreadCount === 0}
          >
            <Icon name="check" size={16} />
            Alles als gelezen markeren
          </button>
        </footer>
      </section>
    </>
  );
}
